import React from 'react';
import { ReportData } from '../../types/report.types';
import HtmlEditor from '../HtmlEditor';

interface ConclusionEditorV2Props {
  reportData: ReportData;
  onHtmlChange: (field: keyof ReportData, value: string) => void;
  onDataChange: (field: string, value: any) => void;
}

const ConclusionEditorV2: React.FC<ConclusionEditorV2Props> = ({ reportData, onHtmlChange, onDataChange }) => {
  let parsedConfig: any = { globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true }, individualFindings: {} };
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const config = parsedConfig;
  const conclusionConfig = config.conclusionV2 || {};

  const targets = reportData.target_details || [];
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  const targetName = reportData.program_details?.[0]?.program_name || reportData.target_details?.[0]?.target_value || 'the target application';

  // Helpers to build dynamic defaults from real findings
  const buildDynamicConclusion = React.useCallback((): string => {
    const allFindings: any[] = reportData.detailed_findings || [];
    const sevOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
    const topFindings = [...allFindings]
      .filter((f: any) => ['critical', 'high'].includes((f.severity_category || f.severity || '').toLowerCase()))
      .sort((a, b) => {
        const aS = (a.severity_category || a.severity || '').toLowerCase();
        const bS = (b.severity_category || b.severity || '').toLowerCase();
        return (sevOrder[aS] || 99) - (sevOrder[bS] || 99);
      });

    const branding = reportData.branding_company || (reportData as any).organization_name || 'Our testing team';

    if (isNetwork) {
      if (topFindings.length > 0) {
        const titles = topFindings.slice(0, 2).map((f: any) => f.title).join(' and ');
        return `The internal network security posture has room for improvement. Testing demonstrated that ${titles} could be leveraged to escalate network privileges and gain unauthorised access to sensitive systems. Prioritising remediation of critical and high findings will substantially reduce overall risk. ${branding} recommends a retest of all remediated items to validate the fixes.`;
      }
      return `The internal network security posture has room for improvement. Prioritising remediation of critical and high findings will substantially reduce overall risk. ${branding} recommends a retest of all remediated items to validate the fixes.`;
    }

    if (topFindings.length > 0) {
      const topTitle = topFindings[0].title || 'the most critical vulnerability';
      const rest = topFindings.slice(1, 3).map((f: any) => f.title).filter(Boolean);
      const restText = rest.length > 0 ? `, alongside ${rest.join(' and ')},` : '';
      return `Overall, the security posture of ${targetName} requires immediate attention. ${topTitle}${restText} ${topFindings.length === 1 ? 'was' : 'were'} identified as the most impactful issue${topFindings.length > 1 ? 's' : ''}, posing a direct risk to the confidentiality, integrity, and availability of the application. Prioritising remediation of critical and high severity findings will substantially reduce overall risk. ${branding} recommends a retest of all remediated items to validate the fixes.`;
    }

    return `Overall, the security posture of ${targetName} is adequate. No critical or high severity issues were identified during the testing window. ${branding} recommends periodic retesting to maintain this posture.`;
  }, [reportData, isNetwork, targetName]);

  const buildDynamicAttackPath = React.useCallback((): string => {
    const allFindings: any[] = reportData.detailed_findings || [];
    const sevOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
    const sorted = [...allFindings].sort((a, b) => {
      const aS = (a.severity_category || a.severity || '').toLowerCase();
      const bS = (b.severity_category || b.severity || '').toLowerCase();
      return (sevOrder[aS] || 99) - (sevOrder[bS] || 99);
    });
    const top = sorted[0];
    if (!top) return 'No critical attack path was identified during the testing window.';
    const topTitle = top.title || 'the highest-severity vulnerability';
    const second = sorted[1];
    const chain = second ? ` then chain it with ${second.title || 'a secondary vulnerability'} to deepen access` : '';
    return `An analysis of the combined findings suggests a probable exploit path: an external attacker could exploit ${topTitle}${chain}, ultimately gaining unauthorised control over sensitive application data or the underlying infrastructure. Addressing the findings in the remediation roadmap will break this chain.`;
  }, [reportData]);

  const MOCK_CONCLUSION_MARKERS = [
    'input validation flaws and access control',
    'weaknesses in endpoint access validation',
    'The assessment revealed areas of security concern',
    'stem from input validation flaws and access control bugs',
    'automate validation checks or leverage credentials',
    'hash-dumping or lateral credential reuse'
  ];
  const isMockConclusion = (text: string) => {
    if (!text) return true;
    const lower = text.toLowerCase();
    return MOCK_CONCLUSION_MARKERS.some(m => lower.includes(m.toLowerCase()));
  };

  React.useEffect(() => {
    const conclusion = reportData.conclusion || '';
    if (!conclusion.trim() || isMockConclusion(conclusion)) {
      onHtmlChange('conclusion', buildDynamicConclusion());
    }
    // Also seed the attack path if it's the hardcoded default
    const existingPath = (parsedConfig.conclusionV2 || {}).attackPath || '';
    if (!existingPath.trim() || isMockConclusion(existingPath)) {
      const newConfig = {
        ...parsedConfig,
        conclusionV2: { ...(parsedConfig.conclusionV2 || {}), attackPath: buildDynamicAttackPath() }
      };
      onDataChange('finding_config', newConfig);
    }
  }, []);


  const attackPathText = conclusionConfig.attackPath !== undefined ? conclusionConfig.attackPath : 'An analysis of the combined findings suggests a probable exploit path: an external attacker could first automate validation checks or leverage credentials, but the roadmap provides remediation details.';

  return (
    <div className="h-full overflow-y-auto space-y-6">
      {/* Posture Description */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Overall Security Posture</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Write the security posture conclusion text</p>
          </div>
        </div>
        <div className="pt-2">
          <HtmlEditor
            field="conclusion"
            title=""
            reportData={reportData}
            onHtmlChange={onHtmlChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ConclusionEditorV2;
